import BrandDropdown from 'shared/components/ui/BrandDropdown';
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import Icon from 'shared/components/Icon';
import { getOperatorsForUserType } from 'features/activity/config/userFilterProperties';
import { getOperatorsForGroupType } from 'features/activity/config/groupFilterProperties';
import { getDataTypeIcon } from 'features/activity/components/UserPropertyFilterModule';
import axiosInstance from 'shared/services/api/axiosInstance';
import FilterDateInput from 'shared/components/filters/FilterDateInput';
import { useUserPropertyServerSearch } from 'features/activity/utils/propertyFilterSearch';

// Native <select> popups are rendered by the OS and ignore the theme entirely,
// so they can't be dark-mode styled. BrandDropdown is the canonical picker.
const TIME_UNIT_OPTIONS = ['minutes', 'hours', 'days', 'weeks', 'months', 'years']
  .map((u) => ({ value: u, label: u }));

const OperatorDropdown = ({ value, operators, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const safeOperators = Array.isArray(operators) && operators.length > 0 ? operators : [{ value: 'is', label: 'Is' }];
  const selectedOperator = safeOperators.find((op) => op.value === value) || safeOperators[0];

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      setPosition({ top: rect.bottom + scrollY + 4, left: rect.left + scrollX, width: rect.width });
    }
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setIsOpen((o) => !o); }}
        className="px-2.5 py-1.5 text-xs bg-surface-hover rounded-lg text-center text-[color:var(--text-primary)] focus:outline-none focus:ring-0 cursor-pointer hover:bg-surface-hover transition-colors min-w-[100px]"
      >
        {selectedOperator.label}
      </button>
      {isOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
            <div
              ref={dropdownRef}
              className="fixed z-[9999] bg-surface border border-line-strong rounded shadow-lg max-h-60 overflow-y-auto"
              style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
                width: `${position.width || 100}px`,
                minWidth: '100px',
              }}
            >
              <div className="py-1">
                {safeOperators.map((op) => (
                  <button
                    key={op.value}
                    type="button"
                    onClick={() => { onChange(op.value); setIsOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-surface-sunken flex items-center ${value === op.value ? 'bg-surface-hover text-[color:var(--text-primary)]' : ''}`}
                  >
                    <span>{op.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

const PropertyMultiselectInput = ({
  value = [],
  options = [],
  onChange,
  placeholder = 'Select values...',
  serverSearch = false,
  onSearchChange,
  searchLoading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const closeDropdown = () => {
    setIsOpen(false);
    setSearchTerm('');
    if (onSearchChange) onSearchChange('');
  };

  const filteredOptions = serverSearch
    ? (options || [])
    : (options || []).filter(
      (opt) =>
        opt.label?.toLowerCase().includes(searchTerm.toLowerCase())
        || opt.value?.toLowerCase().includes(searchTerm.toLowerCase()),
    );

  const toggleOption = (optionValue) => {
    const newValue = value.includes(optionValue) ? value.filter((v) => v !== optionValue) : [...value, optionValue];
    onChange(newValue);
  };

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      setPosition({ top: rect.bottom + scrollY + 4, left: rect.left + scrollX, width: rect.width });
    }
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setIsOpen((o) => !o); }}
        className="px-2.5 py-1.5 text-xs bg-surface-hover rounded-lg text-[color:var(--text-primary)] focus:outline-none focus:ring-0 cursor-pointer hover:bg-surface-hover transition-colors min-w-[140px] text-left flex items-center justify-between gap-1"
      >
        <span className={value.length === 0 ? 'text-[color:var(--text-secondary)]' : ''}>
          {value.length === 0 ? placeholder : `${value.length} selected`}
        </span>
        <ChevronDown className={`h-4 w-4 text-[color:var(--text-secondary)] flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[9998]" onClick={closeDropdown} />
            <div
              ref={dropdownRef}
              className="fixed z-[9999] bg-surface border border-line-strong rounded-lg shadow-lg max-h-60 overflow-y-auto"
              style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
                width: `${Math.max(position.width || 200, 200)}px`,
                minWidth: '200px',
              }}
            >
              <div className="p-2 border-b border-line">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    const next = e.target.value;
                    setSearchTerm(next);
                    if (onSearchChange) onSearchChange(next);
                  }}
                  placeholder="Search..."
                  className="w-full px-2 py-1 text-sm border border-line-strong rounded focus:outline-none focus:ring-2 focus:ring-[color:var(--trodo-primary)] focus:ring-offset-0"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="py-1">
                {searchLoading ? (
                  <div className="px-3 py-2 text-sm text-[color:var(--text-secondary)]">Searching…</div>
                ) : null}
                {!searchLoading && filteredOptions.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-[color:var(--text-secondary)]">No options found</div>
                ) : (
                  !searchLoading ? filteredOptions.map((option) => {
                    const isSelected = value.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => toggleOption(option.value)}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                          isSelected
                            ? 'bg-[color:var(--trodo-primary)] text-ink-onBrand hover:bg-[color:var(--trodo-primary)]'
                            : 'text-[color:var(--text-primary)] hover:bg-surface-sunken'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                          tabIndex={-1}
                          className={`pointer-events-none h-4 w-4 shrink-0 rounded border focus:ring-2 focus:ring-offset-0 ${
                            isSelected
                              ? 'border-white/60 accent-white'
                              : 'border-line-strong accent-[color:var(--trodo-primary)] focus:ring-[color:var(--trodo-primary)]'
                          }`}
                        />
                        <span>{option.label || option.value}</span>
                      </button>
                    );
                  }) : null
                )}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

// Map Users page field names to user-property-options API param
const USER_PROP_TO_API = {
  firstCity: 'first_location_city',
  firstCountry: 'first_location_country',
  lastCity: 'last_location_city',
  lastCountry: 'last_location_country',
  firstReferrer: 'first_referrer',
  lastReferrer: 'last_referrer',
  deviceType: 'device_type',
  browser: 'browser_name',
  operatingSystem: 'os',
  walletType: 'wallet_type',
  walletChain: 'wallet_chain',
  userId: 'user_id',
  email: 'email',
  firstName: 'first_name',
  lastName: 'last_name',
  primaryWallet: 'primary_wallet_address',
};

function resolveUserApiParam(property) {
  if (!property) return null;
  if (USER_PROP_TO_API[property]) return USER_PROP_TO_API[property];
  if (property.startsWith('custom.')) return property.replace('custom.', '');
  if (property.startsWith('user.')) return property.replace('user.', '');
  return null;
}

/**
 * Filter chip for Users page: WHERE → property → operator → value.
 * Same feel as SessionFilterChip / TransactionPropertyFilterChip.
 */
const UserFilterChip = ({
  filter,
  isFirst,
  onEdit,
  onRemove,
  onUpdate,
  teamId,
}) => {
  const connector = filter.connector || (isFirst ? 'WHERE' : 'AND');
  const type = filter.propertyType || 'string';
  const DataTypeIcon = getDataTypeIcon(type);
  const propSrc = filter.propertySource || filter.category;
  const isGroupMembershipChip = propSrc === 'group';
  const operators = isGroupMembershipChip
    ? (type === 'list' ? getOperatorsForGroupType(filter.listItemType || 'string') : getOperatorsForGroupType(type))
    : (type === 'list' ? getOperatorsForUserType(filter.listItemType || 'string') : getOperatorsForUserType(type));
  const defaultOp = operators[0]?.value || 'is';
  const currentOperator = filter.operator || defaultOp;
  const listMode = filter.listMode || 'any_in_list';

  const [propertyOptions, setPropertyOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const isCountFilter = (filter.field || filter.property || '').startsWith('event_count:');

  // Effective options: prefer filter.sampleValues (custom props); else use API-fetched propertyOptions
  const optionsFromSampleValues = (filter.sampleValues && Array.isArray(filter.sampleValues))
    ? filter.sampleValues.map((v) => (typeof v === 'object' && v !== null && 'value' in v ? { value: v.value, label: v.label ?? v.value } : { value: String(v), label: String(v) }))
    : [];
  const hasSampleValues = optionsFromSampleValues.length > 0;
  const effectivePropertyOptions = hasSampleValues ? optionsFromSampleValues : propertyOptions;

  const apiParam = resolveUserApiParam(filter.property);
  const userPropertySearch = useUserPropertyServerSearch({
    filter: {
      ...filter,
      propertySource: 'user',
      property: apiParam ? `user.${apiParam}` : filter.property,
      operator: currentOperator,
      propertyType: type,
      listItemType: filter.listItemType,
    },
    teamId,
    browseOptions: effectivePropertyOptions,
    propertyName: apiParam ? `user.${apiParam}` : null,
  });

  useEffect(() => {
    if (isCountFilter || !filter.property || !teamId) {
      if (!hasSampleValues) setPropertyOptions([]);
      return;
    }
    if (hasSampleValues && (type === 'string' || type === 'list') && (currentOperator === 'is' || currentOperator === 'is_not') && !apiParam) {
      return; // use effectivePropertyOptions from sampleValues, no API
    }
    if (type !== 'string' && !(isGroupMembershipChip && type === 'list')) {
      if (!hasSampleValues) setPropertyOptions([]);
      return;
    }
    if ((type === 'string' || type === 'list') && (currentOperator !== 'is' && currentOperator !== 'is_not')) {
      if (!hasSampleValues) setPropertyOptions([]);
      return;
    }

    const fldG = filter.field || filter.property || '';
    const groupSkipRemoteOptions =
      isGroupMembershipChip &&
      (!filter.groupKey ||
        ['group_key', 'group_id', 'member_count', 'last_updated_at'].includes(fldG));

    if (isGroupMembershipChip) {
      if (groupSkipRemoteOptions) {
        setPropertyOptions([]);
        return;
      }
      const fetchGroupOptions = async () => {
        let numericTeamId = typeof teamId === 'object' && teamId !== null
          ? parseInt(teamId._id || teamId.id, 10)
          : parseInt(teamId, 10);
        if (typeof teamId === 'string' && isNaN(numericTeamId)) {
          try {
            const td = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
            numericTeamId = parseInt(td._id || td.id, 10);
          } catch (_) {}
        }
        if (isNaN(numericTeamId) || !numericTeamId) {
          setPropertyOptions([]);
          return;
        }
        setLoadingOptions(true);
        try {
          const res = await axiosInstance.get('/group-identities/property-values', {
            params: {
              team_id: numericTeamId,
              group_key: filter.groupKey,
              property_key: fldG,
            },
          });
          const opts = res.data?.values || [];
          setPropertyOptions(
            Array.isArray(opts)
              ? opts.map((o) => (typeof o === 'string' ? { value: o, label: o } : { value: o.value ?? o.label, label: o.label ?? o.value }))
              : []
          );
        } catch (_) {
          setPropertyOptions([]);
        } finally {
          setLoadingOptions(false);
        }
      };
      fetchGroupOptions();
      return;
    }

    const resolvedApiParam = apiParam;
    if (!resolvedApiParam) {
      setPropertyOptions([]);
      return;
    }

    const fetchOptions = async () => {
      let numericTeamId = typeof teamId === 'object' && teamId !== null
        ? parseInt(teamId._id || teamId.id, 10)
        : parseInt(teamId, 10);
      if (typeof teamId === 'string' && isNaN(numericTeamId)) {
        try {
          const td = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
          numericTeamId = parseInt(td._id || td.id, 10);
        } catch (_) {}
      }
      if (isNaN(numericTeamId) || !numericTeamId) {
        setPropertyOptions([]);
        return;
      }

      setLoadingOptions(true);
      try {
        const res = await axiosInstance.get('/events/user-property-options', {
          params: { team_id: numericTeamId, property: resolvedApiParam, limit: 500 },
        });
        const opts = res.data?.options || [];
        setPropertyOptions(
          Array.isArray(opts)
            ? opts.map((o) => (typeof o === 'string' ? { value: o, label: o } : { value: o.value ?? o.label, label: o.label ?? o.value }))
            : []
        );
      } catch (_) {
        setPropertyOptions([]);
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchOptions();
  }, [type, currentOperator, filter.property, filter.field, filter.groupKey, teamId, isCountFilter, hasSampleValues, isGroupMembershipChip, apiParam]);

  const multiselectOptions = userPropertySearch.enabled
    ? userPropertySearch.displayOptions
    : effectivePropertyOptions;
  const showMultiselect = userPropertySearch.enabled || multiselectOptions.length > 0;

  const renderValueInput = () => {
    if (type === 'string') {
      if (currentOperator === 'is_set' || currentOperator === 'is_not_set') return null;
      if (currentOperator === 'is' || currentOperator === 'is_not') {
        if (showMultiselect) {
          return (
            <PropertyMultiselectInput
              value={Array.isArray(filter.value) ? filter.value : filter.value != null ? [filter.value] : []}
              options={multiselectOptions}
              onChange={(newValue) => onUpdate({ value: newValue })}
              placeholder={loadingOptions ? 'Loading values...' : 'Select values...'}
              serverSearch={userPropertySearch.enabled}
              onSearchChange={userPropertySearch.setSearchQuery}
              searchLoading={userPropertySearch.searchLoading}
            />
          );
        }
      }
      return (
        <input
          type="text"
          value={filter.value ?? ''}
          onChange={(e) => onUpdate({ value: e.target.value || null })}
          placeholder="Enter value..."
          className="px-2.5 py-1.5 text-xs bg-surface-hover rounded-lg text-center text-[color:var(--text-primary)] focus:outline-none focus:ring-0 hover:bg-surface-hover transition-colors w-24"
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    if (type === 'list') {
      if (currentOperator === 'is_set' || currentOperator === 'is_not_set') return null;
      if (currentOperator === 'is' || currentOperator === 'is_not' || currentOperator === 'contains' || currentOperator === 'does_not_contain') {
        if (showMultiselect && (currentOperator === 'is' || currentOperator === 'is_not')) {
          return (
            <PropertyMultiselectInput
              value={Array.isArray(filter.value) ? filter.value : filter.value != null ? [filter.value] : []}
              options={multiselectOptions}
              onChange={(newValue) => onUpdate({ value: newValue })}
              placeholder={hasSampleValues ? 'Select values...' : (loadingOptions ? 'Loading values...' : 'Select values...')}
              serverSearch={userPropertySearch.enabled}
              onSearchChange={userPropertySearch.setSearchQuery}
              searchLoading={userPropertySearch.searchLoading}
            />
          );
        }
      }
      return (
        <input
          type="text"
          value={Array.isArray(filter.value) ? filter.value.join(', ') : (filter.value ?? '')}
          onChange={(e) => onUpdate({ value: e.target.value.trim() ? e.target.value.split(',').map((s) => s.trim()).filter(Boolean) : null })}
          placeholder="Comma-separated values..."
          className="px-2.5 py-1.5 text-xs bg-surface-hover rounded-lg text-[color:var(--text-primary)] focus:outline-none focus:ring-0 hover:bg-surface-hover transition-colors min-w-[120px]"
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    if (type === 'number') {
      if (currentOperator === 'is_numeric' || currentOperator === 'is_not_numeric') return null;
      if (currentOperator === 'between' || currentOperator === 'not_between') {
        return (
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={filter.value ?? ''}
              onChange={(e) => onUpdate({ value: e.target.value === '' ? null : parseFloat(e.target.value) })}
              placeholder="From..."
              className="px-2.5 py-1.5 text-xs bg-surface-hover rounded-lg text-center text-[color:var(--text-primary)] focus:outline-none focus:ring-0 hover:bg-surface-hover transition-colors w-20"
              onClick={(e) => e.stopPropagation()}
            />
            <span className="text-xs text-[color:var(--text-secondary)]">to</span>
            <input
              type="number"
              value={filter.value2 ?? ''}
              onChange={(e) => onUpdate({ value2: e.target.value === '' ? null : parseFloat(e.target.value) })}
              placeholder="To..."
              className="px-2.5 py-1.5 text-xs bg-surface-hover rounded-lg text-center text-[color:var(--text-primary)] focus:outline-none focus:ring-0 hover:bg-surface-hover transition-colors w-20"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        );
      }
      return (
        <input
          type="number"
          value={filter.value ?? ''}
          onChange={(e) => onUpdate({ value: e.target.value === '' ? null : parseFloat(e.target.value) })}
          placeholder="Value"
          className="px-2.5 py-1.5 text-xs bg-surface-hover rounded-lg text-center text-[color:var(--text-primary)] focus:outline-none focus:ring-0 hover:bg-surface-hover transition-colors w-28"
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    if (type === 'boolean') return null;

    // Object type: show nested properties inline in same view with data type (like Activity > Events)
    if (type === 'object') {
      if (currentOperator === 'is_set' || currentOperator === 'is_not_set') return null;
      const nestedProps = filter.nestedProperties || [];
      if (nestedProps.length > 0) {
        const baseKey = (filter.property || '').replace(/^custom\./, '');
        return (
          <div className="flex items-center gap-1 flex-wrap">
            {/* Nested properties shown inline with data type - all in same view */}
            <div className="flex flex-wrap gap-1">
              {nestedProps.map((np) => {
                const NestedIcon = getDataTypeIcon(np.type?.startsWith('list') ? 'list' : np.type || 'string');
                const isSelected = filter.nestedPropertyKey === np.name;
                return (
                  <button
                    key={np.name}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      const nestedType = np.type || 'string';
                      let listItemType = null;
                      if (nestedType.startsWith('list<') && nestedType.endsWith('>')) {
                        listItemType = nestedType.slice(5, -1);
                      }
                      const fullPath = baseKey ? `custom.${baseKey}.${np.name}` : `custom.${np.name}`;
                      onUpdate({
                        property: fullPath,
                        propertyLabel: baseKey ? `${baseKey}.${np.name}` : np.name,
                        propertyType: nestedType.startsWith('list') ? 'list' : nestedType,
                        listItemType: listItemType || undefined,
                        nestedPropertyKey: np.name,
                        sampleValues: np.sampleValues || [],
                        listMode: nestedType.startsWith('list') ? 'any_in_list' : undefined,
                        value: null,
                      });
                    }}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg transition-colors ${
                      isSelected ? 'bg-[color:var(--trodo-primary)] text-ink-onBrand' : 'bg-surface-hover text-[color:var(--text-primary)] hover:bg-surface-hover'
                    }`}
                  >
                    <NestedIcon className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>{np.name}</span>
                    <span className="text-[10px] opacity-80">{np.type || 'string'}</span>
                  </button>
                );
              })}
            </div>
            {filter.nestedPropertyKey && (() => {
              const nested = nestedProps.find((n) => n.name === filter.nestedPropertyKey);
              const opts = (nested?.sampleValues || []).map((v) =>
                typeof v === 'object' && v?.value != null ? { value: v.value, label: v.label ?? v.value } : { value: String(v), label: String(v) }
              );
              if (opts.length > 0 && (currentOperator === 'is' || currentOperator === 'is_not')) {
                return (
                  <PropertyMultiselectInput
                    value={Array.isArray(filter.value) ? filter.value : filter.value != null ? [filter.value] : []}
                    options={opts}
                    onChange={(v) => onUpdate({ value: v })}
                    placeholder="Select value..."
                  />
                );
              }
              return (
                <input
                  type="text"
                  value={filter.value ?? ''}
                  onChange={(e) => onUpdate({ value: e.target.value || null })}
                  placeholder="Enter value..."
                  className="px-2.5 py-1.5 text-xs bg-surface-hover rounded-lg text-center text-[color:var(--text-primary)] focus:outline-none focus:ring-0 hover:bg-surface-hover w-24"
                  onClick={(e) => e.stopPropagation()}
                />
              );
            })()}
          </div>
        );
      }
      return (
        <input
          type="text"
          value={filter.value ?? ''}
          onChange={(e) => onUpdate({ value: e.target.value || null })}
          placeholder="Path (e.g. key.nested)"
          className="px-2.5 py-1.5 text-xs bg-surface-hover rounded-lg text-[color:var(--text-primary)] focus:outline-none focus:ring-0 hover:bg-surface-hover min-w-[120px]"
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    if (type === 'datetime' || type === 'date') {
      if (currentOperator === 'last' || currentOperator === 'not_in_the_last') {
        return (
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={filter.value ?? ''}
              onChange={(e) => onUpdate({ value: e.target.value === '' ? null : e.target.value })}
              placeholder="X"
              className="px-2.5 py-1.5 text-xs bg-surface-hover rounded-lg text-center text-[color:var(--text-primary)] focus:outline-none focus:ring-0 hover:bg-surface-hover transition-colors w-16"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="w-[104px]" onClick={(e) => e.stopPropagation()}>
              <BrandDropdown
                compact
                value={filter.value2 || 'hours'}
                onChange={(v) => onUpdate({ value2: v })}
                options={TIME_UNIT_OPTIONS}
                ariaLabel="Time unit"
              />
            </div>
            {currentOperator === 'not_in_the_last' && <span className="text-xs text-[color:var(--text-secondary)]">ago</span>}
          </div>
        );
      }
      if (currentOperator === 'between' || currentOperator === 'not_between') {
        // Stack the date range vertically and cap width so it always fits inside the sidebar
        return (
          <div className="flex flex-col gap-1 w-full max-w-[190px]">
            <FilterDateInput
              value={filter.value}
              onChange={(v) => onUpdate({ value: v ? new Date(v).toISOString() : null })}
              inputClassName="w-full max-w-[190px] px-2 py-1.5 text-xs bg-surface-hover rounded-lg text-[color:var(--text-primary)] focus:outline-none focus:ring-0 hover:bg-surface-hover transition-colors"
              onClick={(e) => e.stopPropagation()}
            />
            <span className="text-xs text-[color:var(--text-secondary)] text-center">to</span>
            <FilterDateInput
              value={filter.value2}
              onChange={(v) => onUpdate({ value2: v ? new Date(v).toISOString() : null })}
              inputClassName="w-full max-w-[190px] px-2 py-1.5 text-xs bg-surface-hover rounded-lg text-[color:var(--text-primary)] focus:outline-none focus:ring-0 hover:bg-surface-hover transition-colors"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        );
      }
      if (['on', 'not_on', 'before', 'since', 'in_the_next', 'before_the_last'].includes(currentOperator)) {
        if (currentOperator === 'before_the_last') {
          return (
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={filter.value ?? ''}
                onChange={(e) => onUpdate({ value: e.target.value === '' ? null : e.target.value })}
                placeholder="X"
                className="px-2.5 py-1.5 text-xs bg-surface-hover rounded-lg text-center text-[color:var(--text-primary)] focus:outline-none focus:ring-0 hover:bg-surface-hover transition-colors w-16"
                onClick={(e) => e.stopPropagation()}
              />
              <div className="w-[104px]" onClick={(e) => e.stopPropagation()}>
                <BrandDropdown
                  compact
                  value={filter.value2 || 'hours'}
                  onChange={(v) => onUpdate({ value2: v })}
                  options={TIME_UNIT_OPTIONS}
                  ariaLabel="Time unit"
                />
              </div>
              <span className="text-xs text-[color:var(--text-secondary)]">ago</span>
            </div>
          );
        }
        return (
          <FilterDateInput
            value={filter.value}
            onChange={(v) => onUpdate({ value: v ? new Date(v).toISOString() : null })}
            inputClassName="px-2.5 py-1.5 text-xs bg-surface-hover rounded-lg text-[color:var(--text-primary)] focus:outline-none focus:ring-0 hover:bg-surface-hover transition-colors"
            onClick={(e) => e.stopPropagation()}
          />
        );
      }
    }

    return (
      <input
        type="text"
        value={Array.isArray(filter.value) ? filter.value.join(', ') : (filter.value ?? '')}
        onChange={(e) => onUpdate({ value: e.target.value.trim() || null })}
        placeholder="Value"
        className="px-2.5 py-1.5 text-xs bg-surface-hover rounded-lg text-center text-[color:var(--text-primary)] focus:outline-none focus:ring-0 hover:bg-surface-hover transition-colors min-w-[120px]"
        onClick={(e) => e.stopPropagation()}
      />
    );
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {isFirst ? (
        <span className="text-xs font-medium text-[color:var(--text-secondary)] px-2">WHERE</span>
      ) : (
        <OperatorDropdown
          value={connector}
          operators={[{ value: 'AND', label: 'AND' }, { value: 'OR', label: 'OR' }]}
          onChange={(newConnector) => onUpdate({ connector: newConnector })}
        />
      )}
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit(); } }}
        className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-surface-hover rounded-lg cursor-pointer hover:bg-surface-hover transition-colors"
      >
        <DataTypeIcon className="h-4 w-4 text-[color:var(--text-secondary)] flex-shrink-0" />
        <span
          className="text-[10px] font-semibold uppercase w-5 h-5 inline-flex items-center justify-center rounded bg-surface-hover text-[color:var(--text-secondary)] flex-shrink-0"
          title={isGroupMembershipChip ? 'Group property' : 'User property'}
        >
          {isGroupMembershipChip ? 'G' : 'U'}
        </span>
        <span className="text-sm text-[color:var(--text-primary)] font-medium">{filter.propertyLabel}</span>
      </div>
      {type === 'list' && (
        <OperatorDropdown
          value={listMode}
          operators={[{ value: 'any_in_list', label: 'Any in list' }, { value: 'all_in_list', label: 'All in list' }]}
          onChange={(newListMode) => onUpdate({ listMode: newListMode, value: null })}
        />
      )}
      <OperatorDropdown
        value={currentOperator}
        operators={operators}
        onChange={(newOperator) => {
          const updates = { operator: newOperator, value2: null };
          if (type === 'boolean') {
            updates.value = newOperator === 'is_true';
          } else {
            updates.value = null;
          }
          onUpdate(updates);
        }}
      />
      {renderValueInput()}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="p-1.5 text-[color:var(--text-secondary)] hover:text-[color:var(--text-secondary)] transition-colors"
        title="Delete filter"
      >
        <Icon name="delete" size={16} />
      </button>
    </div>
  );
};

export default UserFilterChip;
